import api from './authService';
import { Sandbox, CreateSandboxForm, LCM, CreateLCMForm } from '../types';

export const sandboxesService = {
  // Get sandboxes for a specific project and revision
  async getSandboxes(projectId: number, revisionId: number): Promise<Sandbox[]> {
    const response = await api.get(`/sandboxes/${projectId}/${revisionId}`);
    return response.data.sandboxes || [];
  },

  // Create a new sandbox
  async createSandbox(projectId: number, revisionId: number, sandboxData: CreateSandboxForm): Promise<{ message: string; sandbox_id: number }> {
    const response = await api.post(`/sandboxes/${projectId}/${revisionId}`, sandboxData);
    return response.data;
  },

  // Get LCMs for a specific sandbox
  async getLCMs(projectId: number, revisionId: number, sandboxId: number): Promise<LCM[]> {
    const response = await api.get(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms`);
    return response.data.lcms || [];
  },

  // Create a new LCM
  async createLCM(projectId: number, revisionId: number, sandboxId: number, lcmData: CreateLCMForm): Promise<{ message: string; lcm_id: number }> {
    const response = await api.post(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms`, lcmData);
    return response.data;
  },

  // Get LCM details
  async getLCMDetails(projectId: number, revisionId: number, sandboxId: number, lcmId: number): Promise<{ lcm: LCM }> {
    const response = await api.get(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms/${lcmId}`);
    return response.data;
  },

  // Check if LCM has an existing matrix
  async checkLCMMatrix(projectId: number, revisionId: number, sandboxId: number, lcmId: number): Promise<{
    has_matrix: boolean;
    task_id?: string;
    completed_at?: string;
  }> {
    const response = await api.get(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms/${lcmId}/matrix`);
    return response.data;
  },

  // Update LCM
  async updateLCM(projectId: number, revisionId: number, sandboxId: number, lcmId: number, lcmData: Partial<CreateLCMForm>): Promise<{ message: string }> {
    const response = await api.put(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms/${lcmId}`, lcmData);
    return response.data;
  },

  // Delete LCM
  async deleteLCM(projectId: number, revisionId: number, sandboxId: number, lcmId: number): Promise<{ message: string }> {
    const response = await api.delete(`/sandboxes/${projectId}/${revisionId}/${sandboxId}/lcms/${lcmId}`);
    return response.data;
  },

  // Copy sandbox using deep_copy_model SQL function
  async copySandbox(originalSandboxId: number, destinationHistoryId: number, newModelName: string): Promise<{ message: string; new_sandbox_id: number }> {
    const response = await api.post('/sandboxes/copy', {
      original_sandbox_id: originalSandboxId,
      destination_history_id: destinationHistoryId,
      new_model_name: newModelName
    });
    return response.data;
  },

  // Generate matrix from LCM config (from LCM Lab)
  async generateMatrix(data: {
    lcm_config: any;
    project_id?: string;
    revision_id?: string;
    sandbox_id?: string;
    lcm_id?: string;
  }): Promise<{ message: string; task_id: string; status: string }> {
    const response = await api.post('/sandboxes/generate-matrix', data);
    return response.data;
  },

  // Generate YMLs from LCM config (from LCM Lab)
  async generateYMLs(data: {
    lcm_config: any;
    yaml_template: string;
    model_name: string;
    project_id?: string;
    revision_id?: string;
    sandbox_id?: string;
    lcm_id?: string;
  }): Promise<{ message: string; task_id: string; status: string }> {
    const response = await api.post('/sandboxes/generate-ymls', data);
    return response.data;
  },

  // Get task status
  async getTaskStatus(taskId: string): Promise<{
    task_id: string;
    status: string;
    progress_percentage?: number;
    result_files?: any[];
    error_message?: string;
  }> {
    const response = await api.get(`/sandboxes/tasks/${taskId}/status`);
    return response.data;
  },

  // Get matrix data from completed task
  async getMatrixData(taskId: string): Promise<{
    matrix_data: any[];
    matrix_columns: string[];
    matrix_hash: string;
  }> {
    const response = await api.get(`/sandboxes/tasks/${taskId}/matrix`);
    return response.data;
  },

  // Get matrix data from completed matrix generation task
  async getTaskMatrixData(taskId: string): Promise<{
    matrix_data: any[];
    combinations_count: number;
  }> {
    const response = await api.get(`/sandboxes/tasks/${taskId}/matrix-data`);
    return response.data;
  },

  // Clean up matrix generation tasks and files
  async cleanupMatrices(data: {
    current_task_id?: string;
    lcm_id: number;
  }): Promise<{ message: string; deleted_count: number }> {
    const response = await api.post('/sandboxes/cleanup-matrices', data);
    return response.data;
  },

  // Download task file as blob (fetches bytes through API, never exposes storage URLs)
  async downloadTaskFile(taskId: string, filename: string): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await api.get(
      `/sandboxes/tasks/${taskId}/download/${encodeURIComponent(filename)}?token=${token}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Legacy helper — prefer downloadTaskFile + openFilePreviewOrDownload
  getDownloadUrl(taskId: string, filename:string): string {
    console.log(`[FE] Creating download URL for task: ${taskId}, file: ${filename}`);

    // Check for performance.memory API
    if ('performance' in window && 'memory' in (window.performance as any)) {
      const memory = (window.performance as any).memory;
      const usedHeap = (memory.usedJSHeapSize / 1024 / 1024).toFixed(2);
      const totalHeap = (memory.totalJSHeapSize / 1024 / 1024).toFixed(2);
      console.log(`[FE] JavaScript Heap Size: ${usedHeap} MB / ${totalHeap} MB`);
    } else {
      console.log("[FE] performance.memory API not available in this browser.");
    }
    
    // Get the base URL from the api instance
    const baseURL = api.defaults.baseURL || '';
    const token = localStorage.getItem('access_token');
    const downloadUrl = `${baseURL}/sandboxes/tasks/${taskId}/download/${filename}?token=${token}`;
    
    console.log(`[FE] Generated URL: ${downloadUrl}`);
    return downloadUrl;
  },
};

export default sandboxesService;